<template>
  <v-card outlined class="rounded-lg pa-6">
    <v-row>
      <v-col cols="12" md="8">
        <h3 class="text-h6 font-weight-bold mb-1" style="color:#1b273e;">Información del producto</h3>
        <p class="text-body-2 grey--text text--darken-1 mb-5">
          Estos datos se mostrarán en el micrositio público del producto.
        </p>

        <v-text-field
          v-model="form.name"
          label="Nombre"
          outlined
          dense
          :counter="80"
          maxlength="80"
          :rules="[v => !!v || 'Requerido']"
        />
        <v-text-field
          v-model="form.shortDescription"
          label="Descripción corta"
          outlined
          dense
          :counter="160"
          maxlength="160"
        />
        <v-textarea
          v-model="form.description"
          label="Descripción"
          outlined
          rows="6"
          auto-grow
        />

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="form.price"
              type="number"
              label="Precio"
              outlined
              dense
              prefix="$"
              min="0"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.currency"
              :items="['MXN','USD']"
              label="Moneda"
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-2" style="color:#1b273e;">Métodos de pago</div>
          <v-chip-group
            v-model="form.paymentMethods"
            multiple
            column
            active-class="primary--text"
          >
            <v-chip
              v-for="m in paymentMethodsOptions"
              :key="m.value"
              :value="m.value"
              filter
              outlined
            >
              {{ m.label }}
            </v-chip>
          </v-chip-group>
        </div>

        <v-switch
          v-model="form.partialPaymentsEnabled"
          label="Permitir plan de pagos parciales"
          color="secondary"
          inset
          hide-details
          class="mb-2"
        />

        <v-switch
          v-model="form.allowNonMembers"
          label="Permitir compra a no-miembros"
          color="secondary"
          inset
          hide-details
          class="mb-4"
        />

        <v-divider class="my-4" />

        <h4 class="text-subtitle-1 font-weight-bold mb-2" style="color:#1b273e;">Audiencia</h4>
        <p class="text-body-2 grey--text text--darken-1 mb-3">
          Define qué miembros pueden ver y comprar este producto.
        </p>
        <AudienceDialog
          ref="audience_configs"
          :productService="product._id"
          :savedRules="savedRules"
        />
        <p class="text-caption mt-2 grey--text text--darken-1" v-if="audiencePreview">
          {{ audiencePreview }}
        </p>

        <v-divider class="my-4" />

        <h4 class="text-subtitle-1 font-weight-bold mb-2" style="color:#1b273e;">Facturación</h4>
        <v-switch
          v-model="form.billingEnabled"
          label="Habilitar facturación para este producto"
          color="secondary"
          inset
          hide-details
          class="mt-0 mb-3"
        />
        <v-row v-if="form.billingEnabled" dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.billingInformation.productKey" label="Clave producto SAT" outlined dense />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.billingInformation.unitKey" label="Clave unidad" outlined dense />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.billingInformation.unitName" label="Unidad" outlined dense />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="form.billingInformation.taxType"
              :items="[
                { text: 'IVA 16%', value: 'IVA_16' },
                { text: 'Exento', value: 'EXENTO' }
              ]"
              label="Tipo de impuesto"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12">
            <v-text-field v-model="form.billingInformation.description" label="Descripción para factura" outlined dense />
          </v-col>
        </v-row>
        <v-alert v-else type="info" outlined dense class="mb-0">
          La facturación está deshabilitada. Las ventas de este producto no generarán CFDI.
        </v-alert>
      </v-col>

      <v-col cols="12" md="4">
        <div class="text-subtitle-2 font-weight-bold mb-2" style="color:#1b273e;">Imagen principal</div>
        <v-img
          :src="bannerPreview"
          aspect-ratio="1"
          class="rounded-lg mb-3"
          style="background:#eef0f4;"
        />
        <v-file-input
          v-model="bannerFile"
          accept="image/*"
          label="Reemplazar imagen principal"
          outlined
          dense
          prepend-icon=""
          prepend-inner-icon="mdi-image-outline"
          @change="onBannerSelected"
        />

        <v-divider class="my-4" />

        <div class="text-subtitle-2 font-weight-bold mb-1" style="color:#1b273e;">Imágenes del carrusel</div>
        <p class="text-caption grey--text text--darken-1 mb-2">Agrega imágenes adicionales que se mostrarán en la galería del micrositio.</p>

        <div v-if="galleryPreviews.length" class="gallery-grid mb-3">
          <div
            v-for="(img, i) in galleryPreviews"
            :key="i"
            class="gallery-grid__item"
          >
            <img :src="img" class="gallery-grid__img" />
            <v-btn
              icon
              x-small
              color="error"
              class="gallery-grid__remove"
              :loading="removingIndex === i"
              @click="removeGallery(img, i)"
            >
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-file-input
          v-model="galleryFiles"
          accept="image/*"
          label="Agregar imágenes"
          outlined
          dense
          multiple
          prepend-icon=""
          prepend-inner-icon="mdi-image-multiple-outline"
          @change="onGallerySelected"
        />
        <v-btn
          v-if="galleryFiles && galleryFiles.length"
          color="secondary"
          depressed
          small
          block
          class="mb-3 font-weight-bold text-none"
          :loading="uploadingGallery"
          @click="uploadGallery"
        >
          <v-icon left small>mdi-upload</v-icon>
          Subir {{ galleryFiles.length }} imagen{{ galleryFiles.length !== 1 ? 'es' : '' }}
        </v-btn>

        <v-divider class="my-4" />

        <div class="text-subtitle-2 font-weight-bold mb-2" style="color:#1b273e;">Acciones</div>
        <v-btn
          color="primaryVariant"
          dark
          block
          depressed
          class="rounded-lg font-weight-bold text-none mb-2"
          :loading="saving"
          @click="save"
        >
          Guardar cambios
        </v-btn>
        <v-btn
          v-if="product.slug"
          outlined
          block
          class="rounded-lg font-weight-bold text-none"
          :href="`/p/${product.slug}`"
          target="_blank"
        >
          <v-icon left>mdi-open-in-new</v-icon> Ver micrositio
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import AudienceDialog from "@/components/dashboard/audienceRules/AudienceDialog.vue";

export default {
  name: "ProductServiceMicrositeTab",
  components: { AudienceDialog },
  props: {
    product: { type: Object, required: true },
  },
  data() {
    return {
      saving: false,
      bannerFile: null,
      bannerPreview: this.product.bannerImage || "https://wechamber-public.s3.amazonaws.com/placeholders/product-service-placeholder.png",
      galleryFiles: null,
      galleryPreviews: (this.product.gallery || []).slice(),
      uploadingGallery: false,
      removingIndex: -1,
      form: this.buildForm(this.product),
      audiencePreview: "",
      savedRules: null,
      paymentMethodsOptions: [
        { value: "CREDIT_CARD", label: "Tarjeta de crédito/débito" },
        { value: "CASH", label: "Efectivo" },
        { value: "BANK_TRANSFER", label: "Transferencia" },
        { value: "PEIBO", label: "Peibo" },
        { value: "MANUALLY_COMPLETED", label: "Marcado manual" },
      ],
    };
  },
  watch: {
    product: {
      deep: true,
      handler(p) {
        this.form = this.buildForm(p);
        this.bannerPreview = p.bannerImage || this.bannerPreview;
      },
    },
  },
  mounted() {
    this.loadSavedRules();
  },
  methods: {
    ...mapActions("productServices", [
      "updateProductService",
      "uploadProductServiceBanner",
      "uploadProductServiceGalleryImage",
      "removeProductServiceGalleryImage",
    ]),
    ...mapActions("audienceRules", ["getAudienceRuleByCategoryId"]),
    ...mapMutations("notification", ["show"]),
    buildForm(p) {
      return {
        name: p.name || "",
        shortDescription: p.shortDescription || "",
        description: p.description || "",
        price: p.price || 0,
        currency: p.currency || "MXN",
        paymentMethods: p.paymentMethods || ["CREDIT_CARD"],
        partialPaymentsEnabled: !!p.partialPaymentsEnabled,
        allowNonMembers: !!p.allowNonMembers,
        billingEnabled: !!p.billingEnabled,
        billingInformation: {
          productKey: (p.billingInformation && p.billingInformation.productKey) || "",
          unitKey: (p.billingInformation && p.billingInformation.unitKey) || "",
          unitName: (p.billingInformation && p.billingInformation.unitName) || "",
          description: (p.billingInformation && p.billingInformation.description) || "",
          taxType: (p.billingInformation && p.billingInformation.taxType) || "IVA_16",
        },
      };
    },
    async loadSavedRules() {
      if (!this.product._id) return;
      const { data, status } = await this.getAudienceRuleByCategoryId(this.product._id);
      if (status && data) {
        const hasRules = Object.keys(data).some(key => data[key]?.length > 0);
        if (hasRules) {
          this.savedRules = data;
        }
      }
    },
    async updateSavedRules() {
      const { data, status } = await this.getAudienceRuleByCategoryId(this.product._id);
      if (status && data) {
        const hasRules = Object.keys(data).some(key => data[key]?.length > 0);
        this.savedRules = hasRules ? data : null;
      }
    },
    onBannerSelected(file) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => { this.bannerPreview = e.target.result; };
      reader.readAsDataURL(file);
    },
    onGallerySelected(files) {
      // preview only — upload happens on button click
    },
    async uploadGallery() {
      if (!this.galleryFiles || !this.galleryFiles.length) return;
      this.uploadingGallery = true;
      for (const file of this.galleryFiles) {
        const res = await this.uploadProductServiceGalleryImage({ id: this.product._id, file });
        if (res.status && res.data && res.data.productService) {
          this.galleryPreviews = (res.data.productService.gallery || []).slice();
        }
      }
      this.galleryFiles = null;
      this.uploadingGallery = false;
    },
    async removeGallery(imageUrl, index) {
      this.removingIndex = index;
      const res = await this.removeProductServiceGalleryImage({ id: this.product._id, imageUrl });
      if (res.status && res.data && res.data.productService) {
        this.galleryPreviews = (res.data.productService.gallery || []).slice();
      }
      this.removingIndex = -1;
    },
    async save() {
      this.saving = true;
      const res = await this.updateProductService({
        id: this.product._id,
        productService: this.form,
      });
      if (res.status && this.bannerFile) {
        await this.uploadProductServiceBanner({ id: this.product._id, file: this.bannerFile });
        this.bannerFile = null;
      }
      if (res.status && this.$refs.audience_configs) {
        try {
          await this.$refs.audience_configs.saveRules({
            productService: this.product._id,
          });
        } catch (e) {
          // no bloquear el guardado del producto por audiencia
        }
      }
      this.saving = false;
      if (res.status) {
        this.show({ text: "Cambios guardados.", color: "success" });
        this.$emit("updated");
        await this.updateSavedRules();
      } else {
        this.show({ text: res.message || "No se pudo guardar.", color: "error" });
      }
    },
  },
};
</script>

<style scoped>
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.gallery-grid__item {
  position: relative;
  aspect-ratio: 1 / 1;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
  overflow: hidden;
}
.gallery-grid__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}
.gallery-grid__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(255,255,255,0.85) !important;
}
</style>
